// The session state machine and span builder. Implements spec/algorithm.md §2–§7.
import {
  DEFAULT_CONFIG,
  type EngineConfig,
  type EventType,
  type IdleReason,
  type SessionResult,
  type SessionState,
  type SessionTotals,
  type Span,
  type TallyEvent,
} from './types.ts';

/** Deduplicate by id (first occurrence wins) and sort by (at, seq, input position). */
export function orderEvents(events: readonly TallyEvent[]): TallyEvent[] {
  const seen = new Set<string>();
  const unique: { event: TallyEvent; index: number }[] = [];
  events.forEach((event, index) => {
    if (seen.has(event.id)) return;
    seen.add(event.id);
    unique.push({ event, index });
  });
  unique.sort((a, b) => a.event.at - b.event.at || (a.event.seq ?? 0) - (b.event.seq ?? 0) || a.index - b.index);
  return unique.map((u) => u.event);
}

/** Prefix reserved for synthetic events; they are never attached to spans. */
export const SYNTHETIC_PREFIX = '~';

/** Events that end or start a turn: tool starts without id before them never pair with a later finish (§4a). */
const TURN_BOUNDARIES: ReadonlySet<EventType> = new Set<EventType>([
  'prompt_submitted', 'turn_completed', 'turn_failed', 'interrupted', 'needs_input', 'session_ended',
]);

/**
 * Tool coverage (§4a): synthetic heartbeats inside every proven tool run
 * (a tool_started followed by a tool_finished with the same toolUseId, or without ids paired in order within a turn).
 * `ordered` must already be deduplicated and sorted.
 */
export function toolCoverage(ordered: readonly TallyEvent[], config: EngineConfig): TallyEvent[] {
  const step = Math.max(1000, Math.floor(config.idleTimeoutMs / 2));
  const starts = new Map<string, TallyEvent>();
  const done = new Set<string>();
  const queue: TallyEvent[] = [];
  let pairs = 0;
  const out: TallyEvent[] = [];
  const cover = (start: TallyEvent, finish: TallyEvent, key: string) => {
    for (let t = start.at + step, k = 1; t < finish.at; t += step, k++) {
      out.push({
        id: `${SYNTHETIC_PREFIX}${key}:${k}`,
        at: t,
        seq: Number.MAX_SAFE_INTEGER,
        provider: finish.provider,
        session: finish.session,
        type: 'heartbeat',
        source: 'inferred',
      });
    }
  };
  for (const e of ordered) {
    if (TURN_BOUNDARIES.has(e.type)) {
      queue.length = 0;
      continue;
    }
    if (e.toolUseId === undefined) {
      if (e.type === 'tool_started') queue.push(e);
      else if (e.type === 'tool_finished') {
        const start = queue.shift();
        if (start) cover(start, e, `hb~${++pairs}`);
      }
      continue;
    }
    if (e.type === 'tool_started') {
      if (!starts.has(e.toolUseId)) starts.set(e.toolUseId, e);
    } else if (e.type === 'tool_finished') {
      const start = starts.get(e.toolUseId);
      if (!start || done.has(e.toolUseId)) continue;
      done.add(e.toolUseId);
      cover(start, e, `hb:${e.toolUseId}`);
    }
  }
  return out;
}

class Machine {
  state: SessionState | null = null;
  reason: IdleReason | null = null;
  stateSince: number | null = null;
  lastActivityAt: number | null = null;
  lastEventAt: number | null = null;
  readonly pending = new Set<string>();
  readonly spans: Span[] = [];
  private open: Span | null = null;
  private readonly config: EngineConfig;

  constructor(config: EngineConfig) {
    this.config = config;
  }

  /** Time-based transitions up to `t` (§5). */
  advance(t: number): void {
    const { idleTimeoutMs, staleGraceMs, inputIdleAfterMs } = this.config;
    if (this.state === 'working' && this.lastActivityAt !== null && t - this.lastActivityAt >= idleTimeoutMs) {
      this.enter('idle', this.lastActivityAt + staleGraceMs, 'stale', null);
    } else if (this.state === 'waiting_input' && this.stateSince !== null && t - this.stateSince >= inputIdleAfterMs) {
      this.enter('idle', this.stateSince + inputIdleAfterMs, 'input_timeout', null);
    }
  }

  /** Event transitions (§4). */
  apply(e: TallyEvent): void {
    if (!e.id.startsWith(SYNTHETIC_PREFIX)) this.lastEventAt = e.at;
    const current = this.state;
    switch (e.type) {
      case 'session_started':
        if (current === null || current === 'ended') this.enter('idle', e.at, null, e);
        else if (current === 'working') this.extend(e);
        else this.attach(e);
        return;
      case 'prompt_submitted':
        this.pending.clear();
        this.enter('working', e.at, null, e);
        return;
      case 'tool_started':
        if (current === 'ended') return;
        if (current === 'waiting_permission') {
          if (e.toolUseId !== undefined) this.pending.delete(e.toolUseId);
          else this.pending.clear();
          this.resumeIfResolved(e);
        } else if (current === 'working') this.extend(e);
        else this.enter('working', e.at, null, e);
        return;
      case 'tool_finished':
        if (current === 'ended') return;
        if (current === 'waiting_permission') {
          this.resolve(e.toolUseId);
          this.resumeIfResolved(e);
        } else if (current === 'working') this.extend(e);
        else this.enter('working', e.at, null, e);
        return;
      case 'permission_requested':
        if (current === 'ended') return;
        this.pending.add(e.toolUseId ?? '*');
        this.enter('waiting_permission', e.at, null, e);
        return;
      case 'permission_resolved':
        if (current === 'ended') return;
        this.resolve(e.toolUseId);
        if (current === 'waiting_permission') this.resumeIfResolved(e);
        else this.attach(e);
        return;
      case 'turn_completed':
      case 'turn_failed':
      case 'interrupted':
      case 'needs_input':
        if (current === 'ended') return;
        this.pending.clear();
        this.enter('waiting_input', e.at, null, e);
        return;
      case 'heartbeat':
        if (current === 'working') this.extend(e);
        else if (current === 'idle' && this.reason === 'stale') this.enter('working', e.at, null, e);
        else this.attach(e);
        return;
      case 'session_ended':
        if (current === 'ended') return;
        this.pending.clear();
        this.enter('ended', e.at, null, e);
        return;
    }
  }

  private resolve(toolUseId: string | undefined): void {
    if (toolUseId === undefined || this.pending.has('*')) this.pending.clear();
    else this.pending.delete(toolUseId);
  }

  private resumeIfResolved(e: TallyEvent): void {
    if (this.pending.size === 0) this.enter('working', e.at, null, e);
    else this.attach(e);
  }

  private attach(e: TallyEvent): void {
    if (e.id.startsWith(SYNTHETIC_PREFIX)) return;
    this.open?.eventIds.push(e.id);
  }

  private extend(e: TallyEvent): void {
    this.lastActivityAt = e.at;
    this.attach(e);
  }

  private enter(next: SessionState, at: number, reason: IdleReason | null, e: TallyEvent | null): void {
    const nextReason = next === 'idle' ? reason : null;
    if (next !== 'ended' && this.state === next && this.reason === nextReason) {
      if (e) this.attach(e);
      if (next === 'working') this.lastActivityAt = at;
      return;
    }
    if (this.open) {
      this.open.end = Math.max(at, this.open.start);
      this.open = null;
    }
    this.state = next;
    this.reason = nextReason;
    this.stateSince = at;
    if (next === 'working') this.lastActivityAt = at;
    if (next === 'ended') return;
    this.open = { state: next, start: at, end: null, reason: nextReason, eventIds: e && !e.id.startsWith(SYNTHETIC_PREFIX) ? [e.id] : [] };
    this.spans.push(this.open);
  }
}

/** Noise filter (§6). Returns new span objects. */
export function filterNoise(spans: readonly Span[], config: EngineConfig): Span[] {
  const kept: Span[] = [];
  let carried: string[] = [];
  for (const span of spans) {
    const copy: Span = { ...span, eventIds: [...carried, ...span.eventIds] };
    if (span.end !== null && span.end - span.start < config.minSpanMs) {
      carried = copy.eventIds;
      continue;
    }
    carried = [];
    kept.push(copy);
  }
  const last = kept[kept.length - 1];
  if (carried.length > 0 && last) last.eventIds.push(...carried);

  const merged: Span[] = [];
  for (const span of kept) {
    const prev = merged[merged.length - 1];
    if (
      prev &&
      prev.end !== null &&
      prev.state === span.state &&
      prev.reason === span.reason &&
      span.start - prev.end < config.mergeGapMs
    ) {
      prev.end = span.end;
      prev.eventIds.push(...span.eventIds);
    } else {
      merged.push(span);
    }
  }
  return merged;
}

export function sessionTotals(spans: readonly Span[], now: number): SessionTotals {
  const totals: SessionTotals = { countedMs: 0, waitingInputMs: 0, waitingPermissionMs: 0, idleMs: 0 };
  for (const span of spans) {
    const duration = Math.max(0, (span.end ?? now) - span.start);
    switch (span.state) {
      case 'working':
        totals.countedMs += duration;
        break;
      case 'waiting_input':
        totals.waitingInputMs += duration;
        break;
      case 'waiting_permission':
        totals.waitingPermissionMs += duration;
        break;
      case 'idle':
        totals.idleMs += duration;
        break;
    }
  }
  return totals;
}

/** Compute one session's spans at `now`. Events must all belong to the same session. */
export function computeSession(
  events: readonly TallyEvent[],
  now: number,
  config: EngineConfig = DEFAULT_CONFIG,
): SessionResult {
  const machine = new Machine(config);
  const ordered = orderEvents(events);
  const coverage = toolCoverage(ordered, config);
  const withCoverage = coverage.length ? orderEvents([...ordered, ...coverage]) : ordered;
  for (const event of withCoverage) {
    machine.advance(event.at);
    machine.apply(event);
  }
  machine.advance(now);
  const spans = filterNoise(machine.spans, config);
  return {
    state: machine.state,
    stateSince: machine.stateSince,
    lastActivityAt: machine.lastActivityAt,
    lastEventAt: machine.lastEventAt,
    spans,
    totals: sessionTotals(spans, now),
  };
}

/** Group events by (provider, session). Key: `${provider}\u0000${session}`. */
export function groupBySession(events: readonly TallyEvent[]): Map<string, TallyEvent[]> {
  const groups = new Map<string, TallyEvent[]>();
  for (const event of events) {
    const key = `${event.provider}\u0000${event.session}`;
    const list = groups.get(key);
    if (list) list.push(event);
    else groups.set(key, [event]);
  }
  return groups;
}
